import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { CheckCircle, Lock, LockOpen } from "@phosphor-icons/react";

function today() { return new Date().toISOString().slice(0, 10); }
function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function Approvals() {
  const { user } = useAuth();
  const { t } = useI18n();
  const role = user?.role;

  const [crews, setCrews] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [weekStart, setWeekStart] = useState(mondayOf(today()));
  const [weekCrew, setWeekCrew] = useState("");
  const [weekEntries, setWeekEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadApprovals = async () => {
    try { const { data } = await api.get("/approvals"); setApprovals(data); } catch { /* ignore */ }
  };

  const [jobcodes, setJobcodes] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/crews"); setCrews(data); } catch { /* ignore */ }
      try { const { data } = await api.get("/jobcodes"); setJobcodes(data); } catch { /* ignore */ }
      await loadApprovals();
    })();
  }, []);

  const jobNames = useMemo(() => Object.fromEntries(jobcodes.map((j) => [j.job_code || j.code, j.job || j.description || j.job_code])), [jobcodes]);

  const loadWeek = async () => {
    const end = addDays(weekStart, 6);
    try {
      const { data } = await api.get(`/time-entries?start_date=${weekStart}&end_date=${end}`);
      setWeekEntries(weekCrew ? data.filter((e) => e.crew_id === weekCrew) : data);
    } catch { setWeekEntries([]); }
  };
  useEffect(() => { loadWeek(); /* eslint-disable-next-line */ }, [weekStart, weekCrew]);

  const weekLocked = useMemo(() => weekEntries.filter((e) => e.locked).length, [weekEntries]);
  const weekUnsigned = useMemo(() => weekEntries.filter((e) => !e.locked && !e.signed), [weekEntries]);
  const unsignedNames = useMemo(() => [...new Set(weekUnsigned.map((e) => e.employee_name || "Unknown"))], [weekUnsigned]);

  const weekSummary = useMemo(() => {
    const m = new Map();
    weekEntries.forEach((e) => {
      const ranch = (e.ranch_name || "—").split("·")[0].trim();
      const key = `${ranch}|${e.job_code || "—"}`;
      const r = m.get(key) || { ranch, task: jobNames[e.job_code] || e.job_code || "—", reg: 0, ot: 0, dt: 0, total: 0 };
      r.reg += e.regular_hours || 0;
      r.ot += e.overtime_hours || 0;
      r.dt += e.doubletime_hours || 0;
      r.total += e.total_hours || 0;
      m.set(key, r);
    });
    return [...m.values()].sort((a, b) => a.ranch.localeCompare(b.ranch) || a.task.localeCompare(b.task));
  }, [weekEntries, jobNames]);
  const weekTotals = useMemo(() => weekSummary.reduce((s, r) => ({ reg: s.reg + r.reg, ot: s.ot + r.ot, dt: s.dt + r.dt, total: s.total + r.total }), { reg: 0, ot: 0, dt: 0, total: 0 }), [weekSummary]);
  const fh = (n) => Math.round(n * 100) / 100;

  const refresh = async () => { await Promise.all([loadWeek(), loadApprovals()]); };

  const approveWeek = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/approvals/weekly", { week_start: weekStart, crew_id: weekCrew || null });
      setMsg({ ok: true, text: `Approved & locked ${data.entries_locked} entries for week of ${weekStart}.` });
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const unlockWeek = async () => {
    if (!window.confirm(`Reopen (unlock) all entries for week of ${weekStart}${weekCrew ? " for this crew" : ""}?`)) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/approvals/unlock", { week_start: weekStart, crew_id: weekCrew || null });
      setMsg({ ok: true, text: `Unlocked ${data.unlocked} entries.` });
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("appr.title")}</h1>
        <p className="text-sm font-medium tracking-wide uppercase text-stone-500">
          {role === "foreman" ? t("appr.sub_foreman") : t("appr.sub_admin")}
        </p>
      </div>

      {msg && (
        <div
          data-testid="approval-message"
          className={`p-4 border text-sm font-medium ${msg.ok ? "bg-[#7C9673]/20 border-[#7C9673] text-[#1F3D34]" : "bg-red-50 border-red-300 text-red-700"}`}
        >
          {msg.text}
        </div>
      )}

      {/* Weekly approval */}
      <div className="bg-white border border-stone-200 p-5 shadow-sm shadow-stone-900/5">
        <div className="flex items-center gap-2 mb-4">
          <Lock weight="bold" size={22} />
          <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("appr.title")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] mb-1 block">{t("appr.week_start")}</label>
            <input data-testid="approval-week-input" type="date" value={weekStart}
              onChange={(e) => setWeekStart(mondayOf(e.target.value))}
              className="w-full h-12 px-3 bg-white border border-stone-200 text-base" />
            <div className="text-[11px] font-mono text-stone-500 mt-1">{weekStart} → {addDays(weekStart, 6)}</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] mb-1 block">{t("appr.crew")}</label>
            <select data-testid="approval-week-crew-select" value={weekCrew} onChange={(e) => setWeekCrew(e.target.value)}
              className="w-full h-12 px-3 bg-white border border-stone-200 text-base">
              <option value="">{role === "foreman" ? t("appr.my_crews") : t("appr.all_crews")}</option>
              {crews.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
        </div>
        {weekSummary.length > 0 && (
          <div className="mt-4 border border-stone-200 overflow-x-auto" data-testid="weekly-hours-summary">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="bg-[#1F3D34] text-white text-left text-[10px] uppercase">
                  <th className="p-2">Ranch</th><th className="p-2">Task</th>
                  <th className="p-2 text-right">Hours</th><th className="p-2 text-right">OT</th>
                  <th className="p-2 text-right">2×OT</th><th className="p-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {weekSummary.map((r, i) => (
                  <tr key={i} className="border-t border-stone-200" data-testid={`summary-row-${i}`}>
                    <td className="p-2 font-bold">{r.ranch}</td>
                    <td className="p-2">{r.task}</td>
                    <td className="p-2 text-right">{fh(r.reg)}</td>
                    <td className={`p-2 text-right ${r.ot > 0 ? "font-bold text-amber-700" : "text-stone-400"}`}>{fh(r.ot)}</td>
                    <td className={`p-2 text-right ${r.dt > 0 ? "font-bold text-red-700" : "text-stone-400"}`}>{fh(r.dt)}</td>
                    <td className="p-2 text-right font-bold">{fh(r.total)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#1F3D34] bg-stone-50 font-bold" data-testid="summary-totals-row">
                  <td className="p-2 uppercase text-xs" colSpan={2}>Week Totals</td>
                  <td className="p-2 text-right">{fh(weekTotals.reg)}</td>
                  <td className="p-2 text-right text-amber-700">{fh(weekTotals.ot)}</td>
                  <td className="p-2 text-right text-red-700">{fh(weekTotals.dt)}</td>
                  <td className="p-2 text-right">{fh(weekTotals.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="text-sm font-mono text-stone-600 flex-1" data-testid="weekly-summary">
            {weekEntries.length} {t("appr.entries")} · {weekLocked} {t("appr.locked")} · {weekUnsigned.length} unsigned
          </div>
          <button data-testid="approve-weekly-btn" disabled={busy || weekEntries.length === 0 || weekLocked === weekEntries.length || weekUnsigned.length > 0}
            onClick={approveWeek}
            className="h-12 px-6 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 text-sm flex items-center justify-center gap-2 disabled:opacity-40">
            <CheckCircle weight="fill" /> {t("appr.approve_week")}
          </button>
          {weekUnsigned.length > 0 && (
            <div className="w-full sm:w-auto sm:order-first bg-amber-50 border border-amber-400 text-amber-900 p-3 text-xs font-semibold" data-testid="unsigned-warning">
              Sign-off required before approval — {weekUnsigned.length} entr{weekUnsigned.length === 1 ? "y" : "ies"} not signed: {unsignedNames.slice(0, 6).join(", ")}{unsignedNames.length > 6 ? "…" : ""}. Employees sign daily or weekly on the Sign-Off page.
            </div>
          )}
          {role === "admin" && weekLocked > 0 && (
            <button data-testid="unlock-week-btn" disabled={busy} onClick={unlockWeek}
              className="h-12 px-4 bg-white text-[#1F3D34] font-bold uppercase border border-stone-200 text-sm flex items-center justify-center gap-2 disabled:opacity-40">
              <LockOpen weight="bold" /> {t("appr.unlock")}
            </button>
          )}
        </div>
      </div>

      {/* History */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight uppercase mb-3" style={{ fontFamily: "'Fraunces', serif" }}>{t("appr.recent")}</h2>
        <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5" data-testid="approval-history">
          {approvals.length === 0 ? (
            <div className="p-6 text-center text-stone-500 font-medium tracking-wide uppercase text-xs">{t("appr.none")}</div>
          ) : (
            approvals.map((a) => (
              <div key={a.id} data-testid={`approval-row-${a.id}`} className="flex items-center gap-3 p-3 border-b border-stone-100">
                <span className="text-[10px] font-bold uppercase px-2 py-1 border bg-[#D8C7A1] border-stone-300">
                  {a.period}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {a.period === "weekly" ? `Week ${a.week_start} → ${a.week_end}` : `Day ${a.work_date}`}
                    {a.crew_name ? ` · ${a.crew_name}` : ""}
                  </div>
                  <div className="text-[11px] font-mono text-stone-500">
                    {a.entry_count} entries · by {a.approved_by_name} ({a.approved_by_role}) · {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
                <Lock weight="fill" size={16} className="text-[#1F3D34] flex-shrink-0" />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
